/**
 * Authentication Service
 * Handles user authentication, login tracking, and session management
 */

import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { ref, set, update, push, get } from 'firebase/database';
import { auth, database, googleProvider } from './firebase';
import { getCurrentTimestamp, formatDateTimeCT } from '../utils/timeUtils';
import { setupPresence, setOffline } from './presenceService';

/**
 * Sign in with Google
 * @returns {Promise<Object>} User object
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Track login
    await trackUserLogin(user);

    return user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export const logOut = async () => {
  try {
    const user = auth.currentUser;

    // Set user offline before signing out
    if (user) {
      await setOffline(user.uid);
    }

    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Track user login event
 * Updates user profile and login history
 * @param {Object} user - Firebase user object
 */
const trackUserLogin = async (user) => {
  if (!user) return;

  const userId = user.uid;
  const timestamp = getCurrentTimestamp();
  const formattedDate = formatDateTimeCT(timestamp);
  const userRef = ref(database, `users/${userId}`);

  try {
    // Check if this is the user's first login
    const userSnapshot = await get(userRef);
    const isFirstLogin = !userSnapshot.exists();

    // Prepare user data
    const userData = {
      email: user.email,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      lastLogin: formattedDate,
      lastLoginTimestamp: timestamp
    };

    // Add firstLogin and createdAt only for new users
    if (isFirstLogin) {
      userData.firstLogin = formattedDate;
      userData.firstLoginTimestamp = timestamp;
      userData.createdAt = formattedDate;
    }

    // Update or create user record
    if (isFirstLogin) {
      await set(userRef, userData);
    } else {
      await update(userRef, {
        lastLogin: formattedDate,
        lastLoginTimestamp: timestamp,
        displayName: user.displayName || userData.displayName,
        photoURL: user.photoURL || userData.photoURL
      });
    }

    // Add login event to history
    const loginHistoryRef = ref(database, `loginHistory/${userId}`);
    const newLoginRef = push(loginHistoryRef);

    await set(newLoginRef, {
      timestamp: timestamp,
      date: formattedDate,
      displayName: user.displayName || 'Anonymous',
      email: user.email
    });

    // Set up presence tracking
    try {
      await setupPresence(userId, {
        displayName: user.displayName || 'Anonymous',
        photoURL: user.photoURL || null
      });
      console.log('Presence tracking set up successfully');
    } catch (presenceError) {
      console.error('Error setting up presence:', presenceError);
      // Don't throw - login should still work even if presence setup fails
    }

    console.log('Login tracked successfully');
  } catch (error) {
    console.error('Error tracking user login:', error);
    // Don't throw - login should still work even if tracking fails
  }
};

/**
 * Subscribe to authentication state changes
 * @param {Function} callback - Called with user object or null
 * @returns {Function} Unsubscribe function
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current authenticated user
 * @returns {Object|null} Current user or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is signed in
 */
export const isAuthenticated = () => {
  return !!auth.currentUser;
};
